import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { manufacturersApi, suppliersApi } from "../api/assets";
import type { ApiError } from "../api/client";
import { formatApiError } from "../lib/apiError";
import { useManufacturers } from "../lib/useManufacturers";
import { useSuppliers } from "../lib/useSuppliers";
import { useAuth } from "../auth/AuthContext";
import { usePrompt } from "./ConfirmDialog";
import { useToast } from "./Toast";
import { Select, type SelectOption } from "./Select";
import Ic from "./icons.jsx";

export type AssetOrigin = {
  /** Row of the manufacturer codebook; "" = not stated. */
  manufacturerId: string;
  manufacturerCode: string;
  /** Row of the supplier codebook — who the piece was bought from; "" = not stated. */
  supplierId: string;
  isCustomMade: boolean;
};

/**
 * Where a piece comes from — either a manufacturer (picked from the codebook,
 * plus its own designation of the piece) and the supplier it was bought from,
 * or the plant's own workshop, never both (Strahinja, 2026-09-10). Switching
 * to "Sopstvena izrada" clears the manufacturer and supplier fields; the
 * backend rejects them on a custom-made asset as well. Neither is ever typed
 * in (sprint 5g, 5h): a missing one is added to its codebook with the "+"
 * button (needs `manufacturers:create` / `suppliers:create`) and is picked at
 * once.
 */
export function AssetOriginFields({ value, onChange, disabled = false }: {
  value: AssetOrigin;
  onChange: (next: AssetOrigin) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const prompt = usePrompt();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { manufacturers, refresh: refreshManufacturers } = useManufacturers();
  const { suppliers, refresh: refreshSuppliers } = useSuppliers();
  const canAddManufacturer = !disabled && hasPermission("manufacturers:create");
  const canAddSupplier = !disabled && hasPermission("suppliers:create");
  const custom = value.isCustomMade;

  const manufacturerOptions = useMemo<SelectOption[]>(
    () => [
      { value: "", label: t("assets.manufacturer.none", "— nije navedeno —") },
      ...manufacturers.map((m) => ({ value: m.id, label: m.name })),
    ],
    [manufacturers, t],
  );
  const supplierOptions = useMemo<SelectOption[]>(
    () => [
      { value: "", label: t("assets.supplier.none", "— nije navedeno —") },
      ...suppliers.map((s) => ({ value: s.id, label: s.name, keywords: [s.taxId, s.city].filter(Boolean).join(" ") })),
    ],
    [suppliers, t],
  );

  async function addManufacturer() {
    const name = await prompt({
      title: t("manufacturers.modal.createTitle", "Novi proizvođač"),
      message: t("manufacturers.prompt.message", "Proizvođač se dodaje u šifarnik i odmah bira za ovaj asset."),
      placeholder: t("manufacturers.field.namePlaceholder", "npr. Sever"),
      confirmLabel: t("action.add", "Dodaj"),
      required: true,
    });
    const trimmed = name?.trim();
    if (!trimmed) return;
    try {
      const created = await manufacturersApi.create({ name: trimmed });
      await refreshManufacturers();
      onChange({ ...value, manufacturerId: created.id });
      toast.success(t("manufacturers.toast.created", "Proizvođač dodat."));
    } catch (err) {
      const e = err as ApiError;
      toast.error(e.code === "NAME_TAKEN"
        ? t("manufacturers.error.nameTaken", "Proizvođač „{{name}}“ već postoji.", { name: trimmed })
        : formatApiError(e));
    }
  }

  async function addSupplier() {
    const name = await prompt({
      title: t("suppliers.modal.createTitle", "Novi dobavljač"),
      message: t("suppliers.prompt.message", "Dobavljač se dodaje u šifarnik samo sa nazivom i odmah bira za ovaj asset. PIB, adresa i ostali podaci se dopisuju na kartonu dobavljača."),
      placeholder: t("suppliers.field.namePlaceholder", "npr. Elektro-Remont d.o.o."),
      confirmLabel: t("action.add", "Dodaj"),
      required: true,
    });
    const trimmed = name?.trim();
    if (!trimmed) return;
    try {
      const created = await suppliersApi.create({ name: trimmed });
      await refreshSuppliers();
      onChange({ ...value, supplierId: created.id });
      toast.success(t("suppliers.toast.created", "Dobavljač dodat."));
    } catch (err) {
      const e = err as ApiError;
      toast.error(e.code === "NAME_TAKEN"
        ? t("suppliers.error.nameTaken", "Dobavljač „{{name}}“ već postoji.", { name: trimmed })
        : formatApiError(e));
    }
  }

  return (
    <>
      <div className="field">
        <span>{t("assets.field.origin", "Poreklo")}</span>
        <div className="chip-group" role="radiogroup" aria-label={t("assets.field.origin", "Poreklo")}>
          <button
            type="button"
            role="radio"
            aria-checked={!custom}
            className={`chip${!custom ? " active" : ""}`}
            disabled={disabled}
            onClick={() => onChange({ ...value, isCustomMade: false })}
          >
            {t("assets.origin.manufacturer", "Proizvođač")}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={custom}
            className={`chip${custom ? " active" : ""}`}
            disabled={disabled}
            onClick={() => onChange({ manufacturerId: "", manufacturerCode: "", supplierId: "", isCustomMade: true })}
          >
            {t("assets.field.isCustomMade", "Sopstvena izrada (radionica)")}
          </button>
        </div>
      </div>
      {!custom && (
        <div className="form-grid">
          <div className="field">
            <span>{t("assets.field.manufacturer", "Proizvođač")}</span>
            <div className="field-with-action">
              <Select
                value={value.manufacturerId}
                options={manufacturerOptions}
                onChange={(id) => onChange({ ...value, manufacturerId: id })}
                disabled={disabled}
                placeholder={t("assets.manufacturer.pick", "Izaberite proizvođača…")}
                ariaLabel={t("assets.field.manufacturer", "Proizvođač")}
              />
              {canAddManufacturer && (
                <span className="tip-wrap" data-tip={t("manufacturers.new", "Novi proizvođač")}>
                  <button type="button" className="btn" onClick={addManufacturer} aria-label={t("manufacturers.new", "Novi proizvođač")}>
                    <Ic.plus />
                  </button>
                </span>
              )}
            </div>
          </div>
          <label className="field">
            <span>{t("assets.field.manufacturerCode", "Oznaka proizvođača")}</span>
            <input className="input" value={value.manufacturerCode} onChange={(e) => onChange({ ...value, manufacturerCode: e.target.value })} disabled={disabled} maxLength={200} placeholder="ZK 132M-4" />
          </label>
          <div className="field">
            <span>{t("assets.field.supplier", "Dobavljač")}</span>
            <div className="field-with-action">
              <Select
                value={value.supplierId}
                options={supplierOptions}
                onChange={(id) => onChange({ ...value, supplierId: id })}
                disabled={disabled}
                placeholder={t("assets.supplier.pick", "Izaberite dobavljača…")}
                ariaLabel={t("assets.field.supplier", "Dobavljač")}
              />
              {canAddSupplier && (
                <span className="tip-wrap" data-tip={t("suppliers.new", "Novi dobavljač")}>
                  <button type="button" className="btn" onClick={addSupplier} aria-label={t("suppliers.new", "Novi dobavljač")}>
                    <Ic.plus />
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
